/**
 * "How to view SFOC": ships and dates, the engine-by-fuel matrix, a curve, the
 * measured SFOC behind it, the time slicer and Play, the values table and its
 * Excel file, two ships side by side, and the link that shares the view.
 *
 * SFOC reads historical data only, so the tour is offered there. The Grid step
 * needs curves on two ships. When the person skips adding a second ship, the
 * tour passes over it.
 */

import {
  activeDataSource,
  modelShips,
  sfocLayout,
  sfocMeasured,
  sfocSelected,
  sfocTables,
} from '../../../state/store.ts';
import { isPageVisible } from '../../../state/page-visibility.ts';
import { sfocSlice } from '../../sfoc/sfoc-measured.ts';
import { parseKey } from '../../sfoc/sfoc-selection.ts';
import { openExploreTab, registerTour } from '../tour-registry.ts';

/** Ships that have a curve on the chart. */
const shipsWithCurves = (): number =>
  new Set(
    [...sfocSelected.value].map((k) => parseKey(k).ship).filter((s) => modelShips.value.includes(s)),
  ).size;
const curveCount = (): number =>
  [...sfocSelected.value].filter((k) => modelShips.value.includes(parseKey(k).ship)).length;

/** What the slicer step measures its change from, set as the step starts. */
let sliceBefore = '';
const sliceKey = (): string => `${sfocSlice.value.min},${sfocSlice.value.max}`;

const q = (sel: string) => (): Element | null => document.querySelector(sel);

registerTour({
  id: 'sfoc',
  version: 1,
  title: 'How to view SFOC',
  description: 'Read the fuel curves of an engine, the measured SFOC behind them, and how they change over time.',
  minutes: 4,
  available: () => isPageVisible('modelviewer') && activeDataSource.value === 'historical',
  prepare: () => openExploreTab('sfoc'),
  steps: [
    {
      target: 'sfoc-ships',
      title: 'Pick a ship',
      text: 'SFOC is the fuel an engine uses for each kWh it makes, in g/kWh. Open the list and choose a ship.',
      advance: { when: () => modelShips.value.length > 0 },
    },
    {
      target: 'sfoc-dates',
      title: 'Choose the dates',
      text: 'The measured points come from these dates. The curves are the versions up to the last day.',
      advance: 'next',
    },
    {
      target: 'sfoc-curves',
      title: 'Which curves exist',
      text: 'One row for each engine, one box for each fuel. A grey box has no curve. A dashed box is a generic curve, not made for this engine.',
      advance: 'next',
    },
    {
      target: 'sfoc-curves',
      title: 'Show a curve',
      text: 'Click one fuel box. Its curve appears on the chart. If the ship has no curves, choose another ship or other dates.',
      advance: { when: () => curveCount() > 0 },
    },
    {
      target: q('[data-tour="sfoc-curves"] .sfoc-mx-row.none'),
      title: 'All fuels of an engine',
      text: 'Click the name of another engine to add all its fuels at once. Click it again to remove them.',
      advance: 'click',
      skipIf: () => !document.querySelector('[data-tour="sfoc-curves"] .sfoc-mx-row.none'),
    },
    {
      target: 'sfoc-measured',
      title: 'What the engine really did',
      text: 'The curve is a model. Measured SFOC shows the real data behind it. Choose Heatmap.',
      advance: { when: () => sfocMeasured.value === 'heatmap' },
    },
    {
      target: 'sfoc-chart',
      title: 'Read the chart',
      text: 'The coloured cells are the measured points. The colour shows how often the engine ran there. Lower SFOC means less fuel for each kWh, not less fuel in total. The lines mark 50% and 80% load.',
      advance: 'next',
    },
    {
      target: 'sfoc-slicer',
      title: 'Move through time',
      text: 'Drag one end of the window inward. The chart then shows only the points inside it, and you can drag the window along. A mark on the track is the day a new curve version started.',
      advance: { when: () => sliceKey() !== sliceBefore },
      enter: () => {
        sliceBefore = sliceKey();
      },
      skippable: true,
    },
    {
      target: q('[data-tour="sfoc-slicer"] .slicer-play'),
      title: 'Play it',
      text: 'Play slides the window across the dates. The curve changes when a new version starts. Double-click the track to see all dates again.',
      advance: 'click',
    },
    {
      target: 'sfoc-values',
      title: 'The numbers',
      text: 'This switch shows the Values table under the chart: the SFOC of each curve from 30% to 90% load. Leave it on.',
      advance: { when: () => sfocTables.value.values },
    },
    {
      target: 'sfoc-values-table',
      title: 'Compare the curves',
      text: 'Click a load, for example 80%, to sort the curves by it.',
      advance: 'next',
    },
    {
      target: q('[data-tour="sfoc-values-table"] .sfoc-download'),
      title: 'Take it to Excel',
      text: 'Click Excel to download this table as it is sorted now.',
      advance: 'click',
    },
    {
      target: 'sfoc-ships',
      title: 'Compare ships',
      text: 'Add a second ship, then click one of its fuel boxes.',
      advance: { when: () => shipsWithCurves() >= 2 },
      skippable: true,
    },
    {
      target: 'sfoc-layout',
      title: 'Side by side',
      text: 'Choose Grid: one chart for each ship, on the same scale.',
      advance: { when: () => sfocLayout.value === 'grid' },
      skipIf: () => shipsWithCurves() < 2,
    },
    {
      target: 'sfoc-chart',
      title: 'Share this view',
      text: 'The address in your browser holds the ships, dates and curves. Copy it and send it, and others see the same chart.',
      advance: 'next',
    },
  ],
});
